import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/auth.service';
import LiveRecord from './LiveRecordCard';

// Hôte dédié du Live (Ubuntu). Tout le trafic viewer/admin vit ici, jamais sur le Mac.
const LIVE_BASE = 'https://live.zitata.tv';

type Mode = 'view' | 'record' | 'admin';

interface LivePerms {
  canView: boolean;
  canRecord: boolean;
  canAdmin: boolean;
}

function getLivePerms(): LivePerms {
  const user = authService.getCurrentUser() || {};
  const isAdmin = String(user.role || '').toLowerCase() === 'admin';
  let ma: any = user.moduleAccess;
  if (typeof ma === 'string') {
    try { ma = JSON.parse(ma); } catch { ma = {}; }
  }
  const live = (ma && ma.live) || {};
  return {
    canView: isAdmin || !!live.view || !!live.stats || !!live.admin,
    canRecord: isAdmin || !!live.record,
    canAdmin: isAdmin || !!live.admin,
  };
}

const barBtn = (active: boolean): React.CSSProperties => ({
  background: active ? '#f59e0b' : '#23262a',
  color: active ? '#121212' : '#cbd5e1',
  border: `1px solid ${active ? '#f59e0b' : '#353a40'}`,
  borderRadius: '8px',
  padding: '8px 16px',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
  transition: 'all 0.2s',
});

const LiveFrame: React.FC<{ mode: Mode }> = ({ mode }) => {
  const navigate = useNavigate();
  const perms = useMemo(getLivePerms, []);
  const [adminUrl, setAdminUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(mode === 'admin');

  // Garde-fou permissions (cohérent avec le gating client-side du reste de l'app)
  const allowed = mode === 'admin' ? perms.canAdmin : mode === 'record' ? perms.canRecord : perms.canView;

  useEffect(() => {
    let cancelled = false;
    if (mode !== 'admin' || !allowed) return;
    setLoading(true);
    setError(null);
    authService.getLiveToken()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.token && res.admin) {
          setAdminUrl(`${LIVE_BASE}/live/admin/?zt=${encodeURIComponent(res.token)}`);
        } else {
          setError(res.message || "Accès à l'administration Live refusé.");
        }
      })
      .catch(() => {
        if (!cancelled) setError("Impossible d'obtenir le jeton d'accès Live.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mode, allowed]);

  // Cache-bust calculé une fois par montage : force l'iframe à recharger une version
  // fraîche de l'embed (évite de rester bloqué sur une ancienne page mise en cache).
  const cacheBust = useMemo(() => Date.now(), []);
  const iframeSrc = mode === 'admin' ? adminUrl : mode === 'view' ? `${LIVE_BASE}/live/embed/?v=${cacheBust}` : null;

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', background: '#121212' }}>
      {/* Barre de menu — bouton Zitata (retour dashboard) à gauche, Live/Admin centrés */}
      <nav style={{
        position: 'relative', display: 'flex', alignItems: 'center',
        background: '#121212', padding: '0 24px', height: '56px',
        borderBottom: '1px solid #23262a', flexShrink: 0, boxSizing: 'border-box',
      }}>
        <div
          onClick={() => navigate('/dashboard')}
          style={{
            background: '#f59e0b', borderRadius: '8px', boxSizing: 'border-box',
            height: '36px', padding: '0 12px', cursor: 'pointer', zIndex: 1,
            display: 'flex', alignItems: 'center',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          title="Retour au tableau de bord"
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{
            color: '#fff', fontWeight: 700, fontSize: '20px', lineHeight: '24px',
            letterSpacing: '-0.5px', fontFamily: 'system-ui, -apple-system, sans-serif',
          }}>Zitata</span>
        </div>

        {/* Groupe centré (centré sur la barre, indépendamment du bouton Zitata) */}
        <div style={{
          position: 'absolute', left: 0, right: 0, display: 'flex',
          justifyContent: 'center', gap: '10px', pointerEvents: 'none',
        }}>
          <div style={{ display: 'flex', gap: '10px', pointerEvents: 'auto' }}>
            {perms.canView && (
              <button style={barBtn(mode === 'view')} onClick={() => navigate('/live-view')}>Live</button>
            )}
            {perms.canRecord && (
              <button style={barBtn(mode === 'record')} onClick={() => navigate('/live-record')}>Record</button>
            )}
            {perms.canAdmin && (
              <button style={barBtn(mode === 'admin')} onClick={() => navigate('/live-admin')}>Admin</button>
            )}
          </div>
        </div>
      </nav>

      <style>{`
        .live-record-card {
          width: min(1280px, 100%, calc((100vh - 220px) * 16 / 9));
          max-width: 100%; box-sizing: border-box;
          padding: 16px 18px; background: #16181b;
          border: 1px solid #23262a; border-radius: 12px;
        }
      `}</style>

      {/* Contenu */}
      <div style={{ flex: 1, position: 'relative' }}>
        {!allowed ? (
          <Centered>
            <h3 style={{ color: '#f59e0b', margin: '0 0 8px' }}>Accès refusé</h3>
            <p style={{ color: '#94a3b8', margin: 0 }}>
              Vous n'avez pas la permission «&nbsp;{mode === 'admin' ? 'Administration' : 'Voir le flux'}&nbsp;» du module Live.
            </p>
          </Centered>
        ) : loading ? (
          <Centered><Spinner /><p style={{ color: '#94a3b8', marginTop: 16 }}>Connexion sécurisée…</p></Centered>
        ) : error ? (
          <Centered>
            <h3 style={{ color: '#f87171', margin: '0 0 8px' }}>Erreur</h3>
            <p style={{ color: '#94a3b8', margin: 0 }}>{error}</p>
          </Centered>
        ) : mode === 'record' ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'auto', padding: 24 }}>
            <LiveRecord />
          </div>
        ) : iframeSrc ? (
          <iframe
            title={mode === 'admin' ? 'Zitata Live — Administration' : 'Zitata Live'}
            src={iframeSrc}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          />
        ) : null}
      </div>
    </div>
  );
};

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 }}>
    {children}
  </div>
);

const Spinner: React.FC = () => (
  <>
    <div style={{ width: 40, height: 40, border: '3px solid #353a40', borderTop: '3px solid #f59e0b', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </>
);

export default LiveFrame;
